import Tracker from "../../../../prop-trackers/tracker.js";
import ValueTracker from "../../../../prop-trackers/value-tracker.js";
import PropTracker from "../../../../prop-trackers/prop-tracker.js";
import TrackerGroup from "../../../../prop-trackers/tracker-group.js";
import SinglePropTracker from "../../../../prop-trackers/single-prop-tracker.js";
import { Mapper, ValueMapper } from "../../../../prop-tracker-utils/mapper.js";
import SinglePropDeepTracker from "../../../../prop-tracker-utils/single-prop-deep-tracker.js";
import copyDictTrackerToProps from "../../../../prop-tracker-utils/copy-dict-tracker-to-props.js";

class SkeletonTrackerCreator extends Tracker {
    constructor(sklDataTracker, evtCtx, shlWrapCtx, shlEvtCtx, skeletonLoader) {
        super();

        this._loadedDeps = shlWrapCtx.getWrapper({});
        this._loadedDepEvents = shlEvtCtx.getEvents(this._loadedDeps);

        this._mapper = new Mapper(sklDataTracker, (key) => new SkeletonMapper(evtCtx, skeletonLoader, this._loadedDeps, this._loadedDepEvents));
        this._mapper.listenNeedsUpdate(() => this.protected_needsUpdate());
    }

    trackObjects(objects, objectEvents) {
        if (objects) {
            this._loadedDeps.objects = objects;
            this._loadedDeps.objectEvents = objectEvents;
        } else {
            delete this._loadedDeps.objects;
            delete this._loadedDeps.objectEvents;
        }
    }

    protected_startUpdate() {
        return this._mapper.startUpdate();
    }

    protected_finishUpdate() {
        this._mapper.finishUpdate();
    }
}

class SkeletonMapper extends ValueMapper {
    constructor(evtCtx, skeletonLoader, loadedDeps, loadedDepEvents) {
        super();

        this._evtCtx = evtCtx;
        this._skeletonLoader = skeletonLoader;
        this._loadedDeps = loadedDeps;
        this._loadedDepEvents = loadedDepEvents;

        this._loadedDepTracker = new PropTracker();
        this._boneArrayTracker = new SinglePropTracker();
        this._boneArrayLengthTracker = new SinglePropTracker();

        this._bindTracker = new BindTracker(evtCtx);
        this._boneTracker = new BoneTracker(evtCtx);
        this._invTracker = new InverseTracker(evtCtx);

        this._skeleton = null;
        this._outputTracker = new ValueTracker();

        const needsUpdate = () => this.protected_needsUpdate();
        this._boneArrayTracker.listenNeedsUpdate(needsUpdate);
        this._boneArrayLengthTracker.listenNeedsUpdate(needsUpdate);
        this._bindTracker.listenNeedsUpdate(needsUpdate);
        this._boneTracker.listenNeedsUpdate(needsUpdate);
        this._invTracker.listenNeedsUpdate(needsUpdate);
    }

    setValue(data) {
        if (data) {
            const events = this._evtCtx.getEvents(data);

            this._loadedDepTracker.track(this._loadedDeps, this._loadedDepEvents);
            this._boneArrayTracker.track(data, events, "bones");

            this._bindTracker.track(data, events, "bind");
            this._invTracker.trackInverses(data, events, "inverses");
        } else {
            this.deleteValue();
        }
    }

    deleteValue() {
        this._loadedDepTracker.track();
        this._boneArrayTracker.track();

        this._bindTracker.track();
        this._invTracker.trackInverses();
    }

    protected_startUpdate() {
        this._loadedDepTracker.updateIfNeeded(({ props }) => {
            const objects = props.get("objects").newValue;
            const objectEvents = props.get("objectEvents").newValue;

            this._boneTracker.trackObjects(objects, objectEvents);
        });

        this._boneArrayTracker.updateIfNeeded(({ prop: { newValue } }) => {
            if (newValue) {
                this._boneArrayLengthTracker.track(newValue, this._evtCtx.getEvents(newValue), "length");
                this._boneTracker.trackData(newValue, this._evtCtx.getEvents(newValue));
                this._invTracker.setBoneCount(newValue.length);
            } else {
                this._boneArrayLengthTracker.track();
                this._boneTracker.trackData();
            }
        });

        this._boneArrayLengthTracker.updateIfNeeded(({ prop: { newExists, newValue } }) => {
            if (newExists) {
                this._invTracker.setBoneCount(newValue);
            } else {
                this._invTracker.clearBoneCount();
            }
        });

        if (this._bindTracker.needsUpdate || this._boneTracker.needsUpdate || this._invTracker.needsUpdate) {
            this._bindTracker.update(({ prop: { newValue: bind } }) => {
                this._boneTracker.update(({ prop: { newValue: bones } }) => {
                    this._invTracker.update(({ prop: { newValue: inverses } }) => {
                        if (this._skeleton) {
                            this._skeletonLoader.dispose(this._skeleton);
                        }

                        if (bind && bones && inverses) {
                            this._skeleton = this._skeletonLoader.create(bind, bones, inverses);
                            this._outputTracker.setValue(this._skeleton);
                        } else {
                            this._skeleton = null;
                            this._outputTracker.deleteValue();
                        }
                    });
                });
            });
        }

        return this._outputTracker.startUpdate();
    }

    protected_finishUpdate() {
        this._outputTracker.finishUpdate();
    }
}

class BindTracker extends Tracker {
    constructor(evtCtx) {
        super();

        this._matrixTracker = new SinglePropDeepTracker(evtCtx);
        this._outputTracker = new ValueTracker();

        const needsUpdate = () => this.protected_needsUpdate();
        this._matrixTracker.listenNeedsUpdate(needsUpdate);
    }

    track(skeletonData, skeletonDataEvents, key) {
        this._matrixTracker.track(skeletonData, skeletonDataEvents, key);
    }

    protected_startUpdate() {
        this._matrixTracker.update(({ prop: { newValue } }) => {
            if (newValue && newValue.length >= 16) {
                this._outputTracker.setValue(newValue);
            } else {
                this._outputTracker.deleteValue();
            }
        });

        return this._outputTracker.startUpdate();
    }

    protected_finishUpdate() {
        this._outputTracker.finishUpdate();
    }
}

class InverseTracker extends Tracker {
    constructor(evtCtx) {
        super();

        this._inversesTracker = new SinglePropDeepTracker(evtCtx);
        this._boneCountTracker = new ValueTracker();
        this._outputTracker = new ValueTracker();

        const needsUpdate = () => this.protected_needsUpdate();
        this._inversesTracker.listenNeedsUpdate(needsUpdate);
        this._boneCountTracker.listenNeedsUpdate(needsUpdate);
    }

    trackInverses(skeletonData, skeletonDataEvents, key) {
        this._inversesTracker.track(skeletonData, skeletonDataEvents, key);
    }

    setBoneCount(count) {
        this._boneCountTracker.setValue(count);
    }

    clearBoneCount() {
        this._boneCountTracker.deleteValue();
    }

    protected_startUpdate() {
        this._inversesTracker.update(({ prop: { newValue: inverses } }) => {
            this._boneCountTracker.update(({ prop: { newExists: countExists, newValue: count } }) => {
                if (inverses && countExists && inverses.length >= count*16) {
                    this._outputTracker.setValue(inverses);
                } else {
                    this._outputTracker.deleteValue();
                }
            });
        });

        return this._outputTracker.startUpdate();
    }

    protected_finishUpdate() {
        this._outputTracker.finishUpdate();
    }
}

class BoneTracker extends Tracker {
    constructor(evtCtx) {
        super();

        this._objects = null;
        this._objectEvents = null;

        this._boneTracker = new PropTracker();
        this._totalCountTracker = new ValueTracker();
        this._loadedCountTracker = new ValueTracker();

        this._boneTrackers = {};
        this._boneTrackerGroup = new TrackerGroup();

        this._loadedCount = 0;
        this._bones = [];
        this._outputTracker = new ValueTracker();

        const needsUpdate = () => this.protected_needsUpdate();
        this._boneTracker.listenNeedsUpdate(needsUpdate);
        this._loadedCountTracker.listenNeedsUpdate(needsUpdate);
        this._boneTrackerGroup.listenNeedsUpdate(needsUpdate);
    }

    trackData(bones, boneEvents) {
        if (bones) {
            this._boneTracker.track(bones, boneEvents);
            this._totalCountTracker.setValue(bones.length);
            this._bones.length = bones.length;
        } else {
            this._boneTracker.track();
            this._totalCountTracker.deleteValue();
            this._bones.length = 0;
        }
    }

    trackObjects(objects, objectEvents) {
        this._objects = objects;
        this._objectEvents = objectEvents;

        for (const key in this._boneTrackers) {
            const tracker = this._boneTrackers[key];
            tracker.track(objects, objectEvents, key);
        }
    }

    protected_startUpdate() {
        this._boneTracker.update(({ props }) => {
            for (const { key, oldExists, newExists, newValue } of props.iterateModified()) {
                if (key === "length") {
                    if (newExists) {
                        this._totalCountTracker.setValue(newValue);
                        this._bones.length = newValue;
                    }
                } else {
                    let tracker;

                    if (!oldExists) {
                        tracker = new SinglePropTracker(key);
                        this._boneTrackers[key] = tracker;
                        this._boneTrackerGroup.add(tracker);
                    } else {
                        tracker = this._boneTrackers[key];
                    }

                    if (newExists) {
                        tracker.track(this._objects, this._objectEvents, newValue);
                    } else {
                        tracker.track();

                        this._boneTrackerGroup.remove(tracker);
                        delete this._boneTrackers[key];
                    }
                }
            }
        });

        this._boneTrackerGroup.update(({ trackers }) => {
            for (const tracker of trackers) {
                tracker.update(({ userdata: key, prop: { oldExists, newExists, newValue } }) => {
                    if (!oldExists) {
                        this._loadedCount++;
                    }

                    if (newExists) {
                        this._bones[key] = newValue;
                    } else {
                        delete this._bones[key];
                        this._loadedCount--;
                    }

                    this._loadedCountTracker.setValue(this._loadedCount);
                });
            }
        });

        this._totalCountTracker.update(({ prop: { newExists: totalExists, newValue: totalCount } }) => {
            this._loadedCountTracker.update(({ prop: { newExists: loadedExists, newValue: loadedCount } }) => {
                if (totalExists && loadedExists && loadedCount === totalCount) {
                    this._outputTracker.setValue(this._bones);
                } else {
                    this._outputTracker.deleteValue();
                }
            });
        });

        return this._outputTracker.startUpdate();
    }

    protected_finishUpdate() {
        this._outputTracker.finishUpdate();
    }
}

export default SkeletonTrackerCreator;
